import React from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import NewsletterForm from './features/NewsletterForm';
import { useLanguage } from '../contexts/LanguageContext';
import { langToSlug } from '../hooks/useLocalePath';
import type { TranslationKey } from '../i18n';

const SOCIAL_LINKS = [
    {
        href: 'https://linkedin.com/company/perceptya',
        title: 'LinkedIn',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
        ),
    },
    {
        href: 'https://instagram.com/perceptya',
        title: 'Instagram',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg>
        ),
    },
    {
        href: 'https://x.com/perceptya',
        title: 'X',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
        ),
    },
    {
        href: 'https://youtube.com/@perceptya',
        title: 'YouTube',
        icon: (
            <svg viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
        ),
    },
];

const Footer: React.FC = () => {
    const { language, t } = useLanguage();
    const lp = (path: string) => `/${langToSlug(language)}${path}`;

    const METHOD_TAGS = [
        t('footer.methodTag1'), t('footer.methodTag2'), t('footer.methodTag3'),
        t('footer.methodTag4'), t('footer.methodTag5'), t('footer.methodTag6'),
        t('footer.methodTag7'), t('footer.methodTag8'),
    ];

    return (
        <footer>
            <div className="footer-inner">

                {/* -- Top: Brand + Nav columns -- */}
                <div className="footer-top">

                    {/* Brand block */}
                    <div className="footer-brand">
                        <Link to={lp('/')} className="footer-logo">
                            <Logo size={28} className="footer-logo-mark" />
                            <span className="footer-logo-text">PERCEPT<em>Y</em>A</span>
                        </Link>
                        <p className="footer-tagline">
                            {t('footer.tagline1')}<br />
                            {t('footer.tagline2')}
                        </p>
                        <div className="footer-social">
                            {SOCIAL_LINKS.map(({ href, title, icon }) => (
                                <a key={title} href={href} title={title} target="_blank" rel="noopener noreferrer">
                                    {icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Nav columns */}
                    <div className="footer-nav">
                        {/* Site */}
                        <div className="footer-col">
                            <div className="footer-col-label">{t('footer.colSite')}</div>
                            <Link to={lp('/')}>{t('footer.linkHome')}</Link>
                            <Link to={lp('/manifesto')}>{t('footer.linkManifesto')}</Link>
                            <Link to={lp('/services')}>{t('footer.linkServices')}</Link>
                            <Link to={lp('/industries')}>{t('footer.linkIndustries')}</Link>
                            <Link to={lp('/contact')}>{t('footer.linkContact')}</Link>
                        </div>

                        {/* Methodology */}
                        <div className="footer-col">
                            <div className="footer-col-label">{t('footer.colMethodology')}</div>
                            <Link to={lp('/services#phase1')}>{t('footer.linkDiscovery')}</Link>
                            <Link to={lp('/services#phase2')}>{t('footer.linkIndustrialization')}</Link>
                            <Link to={lp('/services#engagement')}>{t('footer.linkAccelerators')}</Link>
                            <Link to={lp('/services#engagement')}>{t('footer.linkEngagement')}</Link>
                        </div>

                        {/* Industries */}
                        <div className="footer-col">
                            <div className="footer-col-label">{t('footer.colIndustries')}</div>
                            <Link to={lp('/industries#retail')}>{t('footer.indRetail')}</Link>
                            <Link to={lp('/industries#financial')}>{t('footer.indFinancial')}</Link>
                            <Link to={lp('/industries#logistics')}>{t('footer.indLogistics')}</Link>
                            <Link to={lp('/industries#manufacturing')}>{t('footer.indManufacturing')}</Link>
                            <Link to={lp('/industries#healthcare')}>{t('footer.indHealthcare')}</Link>
                            <Link to={lp('/industries#b2b')}>{t('footer.indB2B')}</Link>
                            <Link to={lp('/industries#startups')}>{t('footer.indStartups')}</Link>
                        </div>

                        {/* Contact */}
                        <div className="footer-col">
                            <div className="footer-col-label">{t('footer.colContact')}</div>
                            <a href="mailto:hello@perceptya.ai">hello@perceptya.ai</a>
                            <Link to={lp('/contact#contact-form')} className="footer-cta">{t('footer.cta')}</Link>
                        </div>
                    </div>

                </div>

                {/* -- Newsletter signup -- */}
                <div className="footer-newsletter">
                    <div className="footer-newsletter-copy">
                        <div className="footer-newsletter-label">{t('newsletter.label' as TranslationKey)}</div>
                        <h3 className="footer-newsletter-headline">{t('newsletter.headline' as TranslationKey)}</h3>
                        <p className="footer-newsletter-sub">{t('newsletter.sub' as TranslationKey)}</p>
                    </div>
                    <div className="footer-newsletter-form">
                        <NewsletterForm />
                    </div>
                </div>

                {/* -- Methodology tags strip -- */}
                <div className="footer-method">
                    <span className="footer-method-label">{t('footer.systemName')}</span>
                    <div className="footer-method-tags">
                        {METHOD_TAGS.map(tag => (
                            <span key={tag} className="footer-method-tag">{tag}</span>
                        ))}
                    </div>
                </div>

                {/* -- Bottom bar -- */}
                <div className="footer-bottom">
                    <span className="footer-copyright">{t('footer.copyright').replace('{year}', String(new Date().getFullYear()))}</span>
                    <div className="footer-bottom-right">
                        <a href="#" className="footer-bottom-link">{t('footer.privacy')}</a>
                        <a href="#" className="footer-bottom-link">{t('footer.terms')}</a>
                        <span className="footer-locale">{t('footer.locale')}</span>
                    </div>
                </div>

            </div>
        </footer>
    );
};

export default Footer;
